import type { Locale } from '~/lib/i18n';

const EXTERNAL_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:'] as const;

/**
 * Extracts the pathname from a full URL if it's from the same origin.
 * Returns null if it's external or can't be determined.
 */
function extractPathnameIfSameOrigin(href: string): string | null {
  try {
    const url = new URL(href);
    // Only handle http/https URLs
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return null;
    }

    // If we're in a browser environment, check if it's the same origin
    if (typeof window !== 'undefined') {
      const currentOrigin = window.location.origin;
      if (url.origin === currentOrigin) {
        return url.pathname;
      }
      // Different origin - treat as external
      return null;
    }

    // Server-side: assume same origin for http/https URLs
    // (routes.marketing.* should always be same origin)
    return url.pathname;
  } catch {
    // Not a valid URL, fall through
  }
  return null;
}

function isExternalHref(href: string): boolean {
  // Hash links are internal
  if (href.startsWith('#')) return false;

  // Absolute URLs or explicit schemes.
  try {
    const url = new URL(href);
    // If it's http/https, check if it's same origin (handled by extractPathnameIfSameOrigin)
    if (url.protocol === 'http:' || url.protocol === 'https:') {
      return false; // Will be handled by extractPathnameIfSameOrigin
    }
    return EXTERNAL_SCHEMES.includes(url.protocol as (typeof EXTERNAL_SCHEMES)[number]);
  } catch {
    // Not an absolute URL; fall through.
  }

  // Protocol-relative URLs or other non-path hrefs.
  return (
    href.startsWith('//') ||
    EXTERNAL_SCHEMES.some((s) => href.startsWith(s))
  );
}

export function withLocalePath(href: string, locale: Locale): string {
  if (!href) return href;

  // Try to extract pathname from full URLs on the same origin
  const pathname = extractPathnameIfSameOrigin(href);
  if (pathname !== null) {
    // Use the extracted pathname
    const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`;
    // Avoid double-prefixing.
    if (normalized === `/${locale}` || normalized.startsWith(`/${locale}/`)) {
      return normalized;
    }
    return `/${locale}${normalized}`;
  }

  // Check if it's truly external
  if (isExternalHref(href)) return href;

  // Ensure leading slash for internal paths.
  const normalized = href.startsWith('/') ? href : `/${href}`;

  // Avoid double-prefixing.
  if (normalized === `/${locale}` || normalized.startsWith(`/${locale}/`)) {
    return normalized;
  }

  return `/${locale}${normalized}`;
}

